import React, { useCallback, useEffect, useId, useState } from 'react';
import { ArrowLeft, ChevronDown, RotateCcw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Modal } from '../common/Modal';
import { listChangeLogs, listClasses, listStaff, listStudents } from './academyApi';
import { AcademyButton, ActionButton, DateInput, SelectInput, TextInput } from './controls';
import { Field } from './modals/form';
import { todayKST } from './periodDates';
import { ErrorNotice, Spinner, useAcademyVersion, useAsync } from './ui';

// 오너가 지정한 화면 진입 번호. 서버 인증을 대신하지 않으며, API는 기존 JWT 권한으로 조회한다.
const ENTRY_PASSWORD = '3720';
const KINDS = [['student', '학생 관리'], ['class', '반 관리'], ['note', '메모'], ['reference', '기준 정보']];
const PER_PAGE = 20;
const initialFilters = () => ({ from: todayKST(), to: todayKST(), actorId: '', studentId: '', classId: '', kind: '' });
const dateTime = new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});
const when = (value) => {
  const date = new Date(value);
  return value && !Number.isNaN(date.getTime()) ? dateTime.format(date) : '시간 정보 없음';
};

function EntryDialog({ onClose, onUnlock }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const formId = useId();
  const errorId = useId();
  const focusInput = useCallback((node) => { node?.focus(); }, []);
  return (
    <Modal
      isOpen onClose={onClose} title="활동 기록 비밀번호" size="sm"
      footer={<div className="flex w-full justify-end gap-2">
        <AcademyButton type="button" variant="secondary" onClick={onClose}>취소</AcademyButton>
        <AcademyButton type="submit" form={formId} variant="primary" disabled={!password}>확인</AcademyButton>
      </div>}
    >
      <form id={formId} className="space-y-3" onSubmit={(e) => {
        e.preventDefault();
        if (password !== ENTRY_PASSWORD) { setError('비밀번호가 맞지 않습니다.'); return; }
        onUnlock();
      }}>
        <Field label="비밀번호">
          <input
            ref={focusInput} type="password" inputMode="numeric" autoComplete="off" maxLength={4}
            value={password} onChange={(e) => { setPassword(e.target.value); setError(''); }}
            className="h-10 w-full rounded-lg border border-gray-300 px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            aria-invalid={!!error} aria-describedby={error ? errorId : undefined}
          />
        </Field>
        {error && <p id={errorId} role="alert" className="text-xs text-danger">{error}</p>}
      </form>
    </Modal>
  );
}

/** 이름으로 검색한 뒤 학생을 선택해야 ID 필터가 된다. 중복 이름은 SID로 구분한다. */
function StudentFilter({ value, onChange }) {
  const { getAccessToken } = useAuth();
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [selectedName, setSelectedName] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setQuery(text.trim()), 300);
    return () => clearTimeout(timer);
  }, [text]);
  const found = useAsync((signal) => query
    ? listStudents({ state: 'all', q: query, perPage: 20, signal }, getAccessToken)
    : Promise.resolve(null), query);
  const currentResults = text.trim() === query;
  return (
    <div className="space-y-2 sm:col-span-2">
      {value ? (
        <div className="flex items-center justify-between gap-2 rounded-lg bg-gray-50 px-3 py-2 text-xs">
          <span>학생: {selectedName} · SID {value}</span>
          <ActionButton onClick={() => { onChange(''); setText(''); setQuery(''); }}>선택 해제</ActionButton>
        </div>
      ) : <>
        <Field label="학생 검색">
          <TextInput value={text} onChange={setText} placeholder="이름 입력 후 검색 결과에서 선택" />
        </Field>
        {!!query && currentResults && !found.loading && !found.error && (
          found.data?.items.length ? <>
            <Field label="검색 결과">
              <SelectInput value="" placeholder="학생 선택" options={found.data.items.map((s) => [s.student_id, `${s.student_name} · SID ${s.student_id}`])}
                onChange={(id) => {
                  if (!id) return;
                  setSelectedName(found.data.items.find((s) => String(s.student_id) === id)?.student_name || '학생');
                  onChange(id);
                }} />
            </Field>
            {found.data.totalItems > 20 && <p className="text-xs text-gray-500">검색 결과가 많습니다. 이름을 더 입력해 주세요.</p>}
          </> : <p role="status" className="text-xs text-gray-500">검색된 학생이 없습니다.</p>
        )}
        {!!text.trim() && (!currentResults || found.loading) && <p role="status" className="text-xs text-gray-500">학생 검색 중…</p>}
        {!!query && currentResults && <ErrorNotice error={found.error} onRetry={found.reload} compact />}
      </>}
    </div>
  );
}

function ActivityRecords() {
  const { getAccessToken } = useAuth();
  const version = useAcademyVersion();
  const [draft, setDraft] = useState(initialFilters);
  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [resetKey, setResetKey] = useState(0);
  const staff = useAsync((signal) => listStaff({ employment: 'all', signal }, getAccessToken), 'activity-staff');
  const classes = useAsync((signal) => listClasses({ state: 'all', signal }, getAccessToken), `activity-classes-${version}`);
  const logs = useAsync((signal) => listChangeLogs({ ...filters, page, perPage: PER_PAGE, signal }, getAccessToken), `${JSON.stringify(filters)}-${page}-${version}`);
  const set = (key) => (value) => setDraft((prev) => ({ ...prev, [key]: value }));
  const validDates = !!draft.from && !!draft.to && draft.from <= draft.to;
  const reset = () => {
    const next = initialFilters();
    setDraft(next); setFilters(next); setPage(1); setResetKey((n) => n + 1);
  };
  return (
    <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
      <form className="grid grid-cols-1 gap-3 rounded-2xl border border-gray-200 p-3 sm:grid-cols-2" onSubmit={(e) => {
        e.preventDefault();
        if (!validDates) return;
        setFilters({ ...draft }); setPage(1); logs.reload();
      }}>
        <Field label="시작일"><DateInput value={draft.from} onChange={set('from')} /></Field>
        <Field label="종료일"><DateInput value={draft.to} onChange={set('to')} /></Field>
        {!validDates && <p role="alert" className="text-xs text-danger sm:col-span-2">시작일과 종료일을 확인해 주세요. 시작일은 종료일보다 늦을 수 없습니다.</p>}
        <div>
          <Field label="작업자"><SelectInput value={draft.actorId} onChange={set('actorId')} placeholder={staff.loading ? '불러오는 중…' : '모든 작업자'} disabled={staff.loading || !!staff.error} options={(staff.data || []).map((s) => [s.staff_id, s.name])} /></Field>
          <ErrorNotice error={staff.error} onRetry={staff.reload} compact />
        </div>
        <Field label="종류"><SelectInput value={draft.kind} onChange={set('kind')} placeholder="모든 종류" options={KINDS} /></Field>
        <div className="sm:col-span-2">
          <Field label="반"><SelectInput value={draft.classId} onChange={set('classId')} placeholder={classes.loading ? '불러오는 중…' : '모든 반'} disabled={classes.loading || !!classes.error} options={(classes.data || []).map((c) => [c.class_id, `${c.name}${c.active ? '' : ' (비활성)'}`])} /></Field>
          <ErrorNotice error={classes.error} onRetry={classes.reload} compact />
        </div>
        <StudentFilter key={resetKey} value={draft.studentId} onChange={set('studentId')} />
        <div className="flex justify-end gap-2 sm:col-span-2">
          <AcademyButton type="button" variant="secondary" onClick={reset}>초기화</AcademyButton>
          <AcademyButton type="submit" variant="primary" disabled={!validDates}>조회</AcademyButton>
        </div>
      </form>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
        <p aria-live="polite">{filters.from} ~ {filters.to}{!logs.loading && logs.data ? ` · 총 ${logs.data.totalItems}건` : ''}</p>
        <ActionButton icon={RotateCcw} onClick={logs.reload} disabled={logs.loading}>새로고침</ActionButton>
      </div>
      {logs.loading ? <Spinner label="활동 기록 불러오는 중" /> : logs.error ? <ErrorNotice error={logs.error} onRetry={logs.reload} /> : <>
        {logs.data?.items.length ? (
          <ul aria-label="활동 기록 목록" className="divide-y divide-gray-100 rounded-2xl border border-gray-200">
            {logs.data.items.map((item) => (
              <li key={item.request_id}>
                <details className="group p-3">
                  <summary className="flex cursor-pointer list-none items-start gap-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary [&::-webkit-details-marker]:hidden">
                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="text-xs text-gray-500"><time dateTime={item.occurred_at || undefined}>{when(item.occurred_at)}</time> · {item.actor?.name || '작업자 정보 없음'}</p>
                      <p className="text-sm font-semibold text-gray-900">{item.label || '변경 기록'}</p>
                      {!!(item.students.length || item.classes.length) && <p className="break-words text-xs text-gray-600">{[...item.students.map((s) => s.student_name), ...item.classes.map((c) => c.class_name)].join(' · ')}</p>}
                      {item.reason && <p className="break-words text-xs text-gray-500">사유: {item.reason}</p>}
                    </div>
                    <ChevronDown aria-hidden="true" className="mt-1 size-4 shrink-0 text-gray-400 transition-transform group-open:rotate-180" />
                  </summary>
                  <ul className="mt-3 space-y-2 border-t border-gray-100 pt-3 text-xs">
                    {item.rows.map((row, index) => <li key={`${row.entity_type}-${row.entity_id}-${index}`} className="space-y-1">
                      <p className="font-medium text-gray-700">{row.entity_label} · {row.action_label}</p>
                      <p className="whitespace-pre-wrap break-words text-gray-600">{row.text || '변경 내용이 없습니다.'}</p>
                      {row.reason && row.reason !== item.reason && <p className="text-gray-500">사유: {row.reason}</p>}
                    </li>)}
                  </ul>
                </details>
              </li>
            ))}
          </ul>
        ) : <p role="status" className="py-12 text-center text-sm text-gray-500">선택한 조건의 활동 기록이 없습니다.</p>}
        {!!logs.data?.totalItems && <nav aria-label="활동 기록 페이지" className="flex items-center justify-center gap-3 text-xs text-gray-600">
          <AcademyButton type="button" disabled={page <= 1} onClick={() => setPage((n) => n - 1)}>이전</AcademyButton>
          <span>{page} / {logs.data.totalPages}</span>
          <AcademyButton type="button" disabled={page >= logs.data.totalPages} onClick={() => setPage((n) => n + 1)}>다음</AcademyButton>
        </nav>}
      </>}
    </div>
  );
}

/** 우측 전용 페이지. 진입·새로고침마다 번호를 확인하고, 통과한 뒤에만 활동 API를 부른다. */
export default function ActivityLogsPage({ onBack }) {
  const [unlocked, setUnlocked] = useState(false);
  return (
    <section aria-label="활동 기록 페이지" className="flex size-full flex-col overflow-hidden rounded-3xl bg-white shadow-lg">
      <div className="flex shrink-0 items-center gap-3 border-b px-4 py-3">
        <ActionButton icon={ArrowLeft} onClick={onBack}>돌아가기</ActionButton>
        <h3 className="text-base font-semibold text-gray-900">활동 기록</h3>
      </div>
      {unlocked ? <ActivityRecords /> : <>
        <p className="p-6 text-center text-sm text-gray-500">비밀번호를 확인한 후 활동 기록을 표시합니다.</p>
        <EntryDialog onClose={onBack} onUnlock={() => setUnlocked(true)} />
      </>}
    </section>
  );
}
